import type { IntrinsicSystemProps, MotionPropsFor } from '@hover/blueprint';
import type {
  AnimatePresenceProps,
  TargetAndTransition,
  Transition,
} from 'framer-motion';
import { matchPath } from 'react-router';

import { Route } from 'src/shared/route';

import { customizeRoutes, fullscreenImageRoutes } from '../constants';

export const isCustomizeRoute = (pathname: string) =>
  !!matchPath(pathname, customizeRoutes);

export const isNewCustomizeRoute = (pathname: string) =>
  !!matchPath(pathname, Route.HOMEOWNER_APP_CUSTOMIZE_NEW.path);

export const isFullscreenImageRoute = (pathname: string) =>
  !!matchPath(pathname, fullscreenImageRoutes);

export const isProjectDetailRoute = (pathname: string) =>
  !!matchPath(pathname, {
    path: Route.HOMEOWNER_APP_PROJECT.path,
    exact: true,
  });

export interface RouteAnimationProps {
  shouldAnimate: boolean;
  containerProps?: MotionPropsFor<IntrinsicSystemProps<'div'>>;
  mode?: AnimatePresenceProps['mode'];
}

export interface RouteAnimationCustom {
  shouldAnimate: boolean;
  to: string;
  from: string;
}

const projectDetailTransition = { duration: 0.5 } satisfies Transition;

export const getRouteAnimation = (
  previousPath: string,
  currentPath: string,
): RouteAnimationProps => {
  if (
    previousPath === currentPath ||
    (isCustomizeRoute(currentPath) && isCustomizeRoute(previousPath))
  ) {
    return { shouldAnimate: false };
  }

  if (isNewCustomizeRoute(currentPath)) {
    return {
      mode: 'sync',
      shouldAnimate: true,
      containerProps: {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
      },
    };
  }

  // Immediately transition since fullscreen routes have layout animations, but we
  // still want nested `AnimatePresence` animations to run
  if (isCustomizeRoute(previousPath) && isFullscreenImageRoute(currentPath)) {
    return {
      mode: 'sync',
      shouldAnimate: true,
      containerProps: {
        initial: { opacity: 1 },
        animate: { opacity: 1 },
        transition: { duration: 0 },
      },
    };
  }

  // No transition at all for routes that just have layout animations
  if (
    isFullscreenImageRoute(previousPath) ||
    isFullscreenImageRoute(currentPath) ||
    isCustomizeRoute(currentPath)
  ) {
    return { shouldAnimate: false };
  }

  const isEnteringProjectDetail = isProjectDetailRoute(currentPath);
  const isExitingCustomize =
    isCustomizeRoute(previousPath) && !isCustomizeRoute(currentPath);

  // Simple cross-fade instead of the default fade + slide
  if (isEnteringProjectDetail || isExitingCustomize) {
    return {
      mode: 'wait',
      shouldAnimate: true,
      containerProps: {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        // Project details has a specific transition
        ...(isEnteringProjectDetail && { transition: projectDetailTransition }),
      },
    };
  }

  return { shouldAnimate: true, mode: 'wait' };
};

export const getRouteExitAnimation = (
  previousPath: string,
  currentPath: string,
): TargetAndTransition => {
  // Simple fade instead of the default fade + slide
  if (
    (isFullscreenImageRoute(previousPath) && isCustomizeRoute(currentPath)) ||
    isCustomizeRoute(previousPath)
  ) {
    return { opacity: 0 };
  }

  // Project details has a specific transition
  if (isProjectDetailRoute(previousPath)) {
    return { opacity: 0, transition: projectDetailTransition };
  }

  return { opacity: 0, y: 50 };
};
